import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Upload, Users, FileType, Settings, LogOut, UserCog, FileText } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { DynamicLogo } from "../../components/DynamicLogo";
import { DocumentUpload } from "./components/DocumentUpload";
import { ClientManagement } from "./components/ClientManagement";
import { TipoDocumentoManagement } from "./components/TipoDocumentoManagement";
import { UserManagement } from "./components/UserManagement";
import { PropostaManagement } from "./components/PropostaManagement";
import { EmpresaConfig } from "./components/EmpresaConfig";

export function Dashboard() {
  const [activeTab, setActiveTab] = useState("documents");
  const { user, isAuthenticated, isAdmin, logout } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      window.location.href = "/admin/login";
    } else if (!isAdmin) {
      window.location.href = "/portal";
    }
  }, [isAuthenticated, isAdmin]);

  const handleLogout = () => {
    logout();
    window.location.href = "/admin/login";
  };

  if (!isAuthenticated || !isAdmin) {
    return null;
  }

  const tabs = [
    { id: "documents", label: "Documentos", icon: Upload },
    { id: "propostas", label: "Propostas", icon: FileText },
    { id: "clients", label: "Clientes", icon: Users },
    { id: "tipos", label: "Tipos de Documento", icon: FileType },
    { id: "users", label: "Usuários", icon: UserCog },
    { id: "settings", label: "Configurações", icon: Settings },
  ];

  return (
    <>
      <Helmet>
        <title>Dashboard Admin - Arcgel</title>
      </Helmet>

      <div className="min-h-screen bg-gray-100 flex">
        {/* Sidebar */}
        <div className="w-64 bg-slate-900 text-white flex flex-col">
          <div className="p-6 border-b border-slate-700">
            <DynamicLogo alt="Arcgel" className="h-8 w-auto mx-auto"
              fallback={<div className="h-8 w-16 bg-cyan-600 rounded flex items-center justify-center text-white font-bold text-xs">ARCGEL</div>} />
          </div>

          <div className="px-4 py-3 border-b border-slate-700">
            <p className="text-sm text-slate-400">Logado como</p>
            <p className="font-medium text-white truncate">{user?.name}</p>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            {tabs.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${activeTab === tab.id ? "bg-cyan-600 text-white" : "text-slate-300 hover:bg-slate-800"}`}>
                <tab.icon className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{tab.label}</span>
              </button>
            ))}
          </nav>

          <div className="p-4 border-t border-slate-700">
            <button onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors">
              <LogOut className="w-5 h-5" />
              Sair
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          <header className="bg-white border-b border-gray-200 px-6 py-4">
            <h1 className="text-2xl font-bold text-gray-900">
              {tabs.find((t) => t.id === activeTab)?.label}
            </h1>
          </header>

          <main className="flex-1 p-6 overflow-auto">
            {activeTab === "documents" && <DocumentUpload />}
            {activeTab === "propostas" && <PropostaManagement onNavigateToClients={() => setActiveTab("clients")} />}
            {activeTab === "clients" && <ClientManagement />}
            {activeTab === "tipos" && <TipoDocumentoManagement />}
            {activeTab === "users" && <UserManagement />}
            {activeTab === "settings" && <EmpresaConfig />}
          </main>
        </div>
      </div>
    </>
  );
}
